import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Box,
  Grid,
  Alert,
  Card,
  CardContent,
  CircularProgress,
  Link
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { organisationsApi, organisationContactsApi } from '../services/api';

const OrganisationsPage = () => {
  const [organisations, setOrganisations] = useState([]);
  const [filteredOrganisations, setFilteredOrganisations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [errorProjects, setErrorProjects] = useState([]);  // Projects causing constraint error
  const [searchTerm, setSearchTerm] = useState('');
  
  // Dialog states
  const [openDialog, setOpenDialog] = useState(false);
  const [editingOrganisation, setEditingOrganisation] = useState(null);
  const [organisationForm, setOrganisationForm] = useState({
    name: '',
    address: '',
    email: '',
    phone: ''
  });

  // Contacts dialog
  const [contactsDialog, setContactsDialog] = useState(false);
  const [selectedOrganisation, setSelectedOrganisation] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [contactError, setContactError] = useState('');  // Error specific to contacts dialog
  const [contactErrorProjects, setContactErrorProjects] = useState([]);  // Projects causing contact constraint error
  
  // Contact form dialog
  const [contactFormDialog, setContactFormDialog] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [contactForm, setContactForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    mobile: ''
  });

  useEffect(() => {
    fetchOrganisations();
  }, []);

  useEffect(() => {
    // Filter organisations based on search term
    if (searchTerm.trim() === '') {
      setFilteredOrganisations(organisations);
    } else {
      const filtered = organisations.filter(org =>
        org.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        org.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredOrganisations(filtered);
    }
  }, [searchTerm, organisations]);

  const fetchOrganisations = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await organisationsApi.getAll();
      setOrganisations(response.data);
    } catch (err) {
      console.error('Error fetching organisations:', err);
      setError('Failed to fetch organisations');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (organisation = null) => {
    if (organisation) {
      setEditingOrganisation(organisation);
      setOrganisationForm({
        name: organisation.name || '',
        address: organisation.address || '',
        email: organisation.email || '',
        phone: organisation.phone || ''
      });
    } else {
      setEditingOrganisation(null);
      setOrganisationForm({
        name: '',
        address: '',
        email: '',
        phone: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingOrganisation(null);
    setOrganisationForm({
      name: '',
      address: '',
      email: '',
      phone: ''
    });
  };

  const handleFormChange = (field, value) => {
    setOrganisationForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveOrganisation = async () => {
    try {
      if (editingOrganisation) {
        // Update existing organisation
        await organisationsApi.update(editingOrganisation.organisationid, organisationForm);
      } else {
        // Create new organisation
        await organisationsApi.create(organisationForm);
      }
      
      handleCloseDialog();
      fetchOrganisations();
      setError('');
    } catch (err) {
      setError('Failed to save organisation');
      console.error(err);
    }
  };

  const handleDeleteOrganisation = async (organisationId) => {
    if (window.confirm('Are you sure you want to delete this organisation? This action cannot be undone.')) {
      try {
        await organisationsApi.delete(organisationId);
        fetchOrganisations();
        setError('');
        setErrorProjects([]);
      } catch (err) {
        // Check if error response has project constraint details
        if (err.response && err.response.data) {
          const data = err.response.data;
          if (data.constraint_type === 'projects' && data.projects) {
            setError(data.error);
            setErrorProjects(data.projects);
          } else if (data.error) {
            setError(data.error);
            setErrorProjects([]);
          } else {
            setError('Failed to delete organisation');
            setErrorProjects([]);
          }
        } else {
          setError('Failed to delete organisation');
          setErrorProjects([]);
        }
        console.error(err);
      }
    }
  };

  const handleViewContacts = async (organisation) => {
    try {
      setSelectedOrganisation(organisation);
      setContactError('');
      setContactErrorProjects([]);
      const response = await organisationsApi.getContacts(organisation.organisationid);
      setContacts(response.data.contacts || []);
      setContactsDialog(true);
    } catch (err) {
      setError('Failed to fetch contacts');
      console.error(err);
    }
  };

  const handleContactFormChange = (e) => {
    const { name, value } = e.target;
    setContactForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleOpenContactForm = (contact = null) => {
    if (contact) {
      setEditingContact(contact);
      setContactForm({
        first_name: contact.first_name || '',
        last_name: contact.last_name || '',
        email: contact.email || '',
        mobile: contact.mobile || ''
      });
    } else {
      setEditingContact(null);
      setContactForm({
        first_name: '',
        last_name: '',
        email: '',
        mobile: ''
      });
    }
    setContactFormDialog(true);
  };

  const handleCloseContactForm = () => {
    setContactFormDialog(false);
    setEditingContact(null);
    setContactForm({
      first_name: '',
      last_name: '',
      email: '',
      mobile: ''
    });
  };

  const handleSaveContact = async () => {
    try {
      const contactData = {
        ...contactForm,
        organisation_id: selectedOrganisation.organisationid
      };

      if (editingContact) {
        // Update existing contact
        await organisationContactsApi.update(editingContact.organisationcontactid, contactData);
      } else {
        // Create new contact
        await organisationContactsApi.create(contactData);
      }
      
      handleCloseContactForm();
      // Refresh contacts list
      const response = await organisationsApi.getContacts(selectedOrganisation.organisationid);
      setContacts(response.data.contacts || []);
      
      setError('');
    } catch (err) {
      setError('Failed to save contact');
      console.error(err);
    }
  };

  const handleDeleteContact = async (contactId) => {
    if (window.confirm('Are you sure you want to delete this contact? This action cannot be undone.')) {
      try {
        await organisationContactsApi.delete(contactId);
        // Refresh contacts list
        const response = await organisationsApi.getContacts(selectedOrganisation.organisationid);
        setContacts(response.data.contacts || []);
        
        setContactError('');
        setContactErrorProjects([]);
      } catch (err) {
        // Check if error response has project constraint details
        if (err.response && err.response.data) {
          const data = err.response.data;
          if (data.constraint_type === 'projects' && data.projects) {
            setContactError(data.error);
            setContactErrorProjects(data.projects);
          } else if (data.error) {
            setContactError(data.error);
            setContactErrorProjects([]);
          } else {
            setContactError('Failed to delete contact');
            setContactErrorProjects([]);
          }
        } else {
          setContactError('Failed to delete contact');
          setContactErrorProjects([]);
        }
        console.error(err);
      }
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Organisation Management
        </Typography>
        <Typography variant="body1" color="text.secondary" gutterBottom>
          Manage your client organisations and their contact details
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => { setError(''); setErrorProjects([]); }}>
          <Typography>{error}</Typography>
          {errorProjects.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Affected projects:</Typography>
              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                {errorProjects.map((project) => (
                  <li key={project.project_id}>
                    <Link
                      component={RouterLink}
                      to={`/projects/${project.project_id}`}
                      sx={{ fontWeight: 'medium' }}
                    >
                      {project.project_no}
                    </Link>
                  </li>
                ))}
              </Box>
            </Box>
          )}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Search organisations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenDialog()}
                >
                  Add Organisation
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Organisation Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Address</TableCell>
              <TableCell>Contacts</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredOrganisations.map((organisation) => (
              <TableRow key={organisation.organisationid}>
                <TableCell>
                  <Typography variant="subtitle1" fontWeight="medium">
                    {organisation.name}
                  </Typography>
                </TableCell>
                <TableCell>{organisation.email || '-'}</TableCell>
                <TableCell>{organisation.phone || '-'}</TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {organisation.address || '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Button
                    size="small"
                    startIcon={<PersonIcon />}
                    onClick={() => handleViewContacts(organisation)}
                    variant="outlined"
                  >
                    View Contacts
                  </Button>
                </TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    onClick={() => handleOpenDialog(organisation)}
                    color="primary"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteOrganisation(organisation.organisationid)}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {filteredOrganisations.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body1" color="text.secondary">
                    {searchTerm ? 'No organisations found matching your search' : 'No organisations found'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Organisation Form Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingOrganisation ? 'Edit Organisation' : 'Add New Organisation'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Organisation Name"
              value={organisationForm.name}
              onChange={(e) => handleFormChange('name', e.target.value)}
              sx={{ mb: 2 }}
              required
            />
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={organisationForm.email}
              onChange={(e) => handleFormChange('email', e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Phone"
              value={organisationForm.phone}
              onChange={(e) => handleFormChange('phone', e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Address"
              multiline
              rows={3}
              value={organisationForm.address}
              onChange={(e) => handleFormChange('address', e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={handleSaveOrganisation} 
            variant="contained"
            disabled={!organisationForm.name.trim()}
          >
            {editingOrganisation ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Contacts Dialog */}
      <Dialog open={contactsDialog} onClose={() => { setContactsDialog(false); setContactError(''); setContactErrorProjects([]); }} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              Contacts for {selectedOrganisation?.name}
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenContactForm()}
              size="small"
            >
              Add Contact
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          {contactError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => { setContactError(''); setContactErrorProjects([]); }}>
              <Typography>{contactError}</Typography>
              {contactErrorProjects.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Affected projects:</Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2 }}>
                    {contactErrorProjects.map((project) => (
                      <li key={project.project_id}>
                        <Link
                          component={RouterLink}
                          to={`/projects/${project.project_id}`}
                          sx={{ fontWeight: 'medium' }}
                        >
                          {project.project_no}
                        </Link>
                      </li>
                    ))}
                  </Box>
                </Box>
              )}
            </Alert>
          )}
          {contacts.length > 0 ? (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Mobile</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {contacts.map((contact) => (
                    <TableRow key={contact.organisationcontactid}>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                          {contact.full_name}
                        </Box>
                      </TableCell>
                      <TableCell>{contact.email || '-'}</TableCell>
                      <TableCell>{contact.mobile || '-'}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenContactForm(contact)}
                          color="primary"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteContact(contact.organisationcontactid)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box textAlign="center" py={4}>
              <PersonIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No contacts found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Add the first contact for this organisation
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenContactForm()}
              >
                Add Contact
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setContactsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Contact Form Dialog */}
      <Dialog open={contactFormDialog} onClose={handleCloseContactForm} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingContact ? 'Edit Contact' : 'Add Contact'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label="First Name"
                name="first_name"
                value={contactForm.first_name}
                onChange={handleContactFormChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label="Last Name"
                name="last_name"
                value={contactForm.last_name}
                onChange={handleContactFormChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={contactForm.email}
                onChange={handleContactFormChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Mobile"
                name="mobile"
                value={contactForm.mobile}
                onChange={handleContactFormChange}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseContactForm}>Cancel</Button>
          <Button 
            onClick={handleSaveContact} 
            variant="contained"
            disabled={!contactForm.first_name || !contactForm.last_name}
          >
            {editingContact ? 'Update' : 'Add'} Contact
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default OrganisationsPage;
